import {
  Component,
  Input,
  OnInit,
  OnChanges,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  ControlsOf,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';
import {
  PhysicalStates,
  SpecialWasteProfile,
  SpecialWasteProfileModel,
  WasteStreamOptions,
} from '@digital-platform/waste-profiles/domain';

type WasteStreamFormElements = Pick<
  SpecialWasteProfile,
  | 'name'
  | 'stateApprovalWasteCode'
  | 'stateApprovalWasteCodeNotApplicable'
  | 'wasteType'
  | 'methodOfShipment'
  | 'frequency'
  | 'disposalConsideration'
  | 'physicalState'
  | 'estimatedVolume'
  | 'unitOfMeasure'
  | 'processGeneratingWaste'
  | 'percentSolids'
  | 'percentSolidsLow'
  | 'percentSolidsHigh'
  | 'percentSolidNotApplicable'
  | 'phRange'
  | 'phExact'
  | 'phNotApplicable'
  | 'flashPoint'
  | 'flashPointRange'
  | 'flashPointNotApplicable'
  | 'containsFreeLiquids'
  | 'otherMethodOfShipment'
>;

type FormControls = ControlsOf<WasteStreamFormElements>;

@Component({
  selector: 'digital-platform-waste-stream-form',
  templateUrl: './waste-stream-form.component.html',
  styleUrls: ['./waste-stream-form.component.scss'],
})
export class WasteStreamFormComponent
  extends NestedFormComponent<WasteStreamFormElements, FormControls>
  implements OnInit, OnChanges, ControlValueAccessor<WasteStreamFormElements>
{
  public form: FormGroup<FormControls>;
  @Input() profileStatus: string;
  @Input() isLegacy = false;
  @Input() tabIndex = 1;

  rangeOptions = {
    rangeValues: ['≤2', '>2 - 5', '5 - 9', '9 - <12.5', '≥12.5'],
  };

  flashPointRangeOptions = {
    rangeValues: ['<140', '140-150', '>150'],
  };

  volumeLabel = 'Estimated Annual Volume';
  isLiquid = false;
  flashPointNotApplicableNotVisiable = false;
  isPhNotApplicable = false;
  showOtherShipment = false;
  showDeepWell = false;
  showBagged = true;
  isWasteCodeRequired = true;
  showSolidification = true;
  isSolidToggle = false;
  isSolidToggleEnabled = false;
  isphToggle = false;
  isphToggleEnabled = false;
  isFlashPointToggle = false;
  isFlashPointToggleEnabled = false;
  disableFreeliquids = false;
  charCount = 0;
  maxCharCount = 500;

  landFillOption = { label: 'Landfill', value: 'landfill' };
  solidificationOptions = { label: 'Solidification', value: 'solidification' };
  biomremediationOptions = {
    label: 'Bioremediation',
    value: 'biomremediation',
  };
  deepWellOptions = { label: 'Deep Well', value: 'deep-well' };

  filteredDisposalConsideration = [];
  streamOptions: WasteStreamOptions = {
    options: [
      'Cubic Yards',
      'Tons',
      'Gallons',
      'Drums',
      'Pounds',
      'Pallets',
      'Totes',
    ],
  };
  physicalStates = PhysicalStates;

  constructor(
    private formBuilder: FormBuilder,
    @Optional() @Self() public controlDir: NgControl
  ) {
    super(controlDir);
  }

  initializeForm(wasteStream: WasteStreamFormElements) {
    const hasAnyPh = (): boolean =>
      !!this.form.get(['phExact']).value || !!this.form.get(['phRange']).value;
    const hasAnyFlashPoint = (): boolean =>
      !!this.form.get(['flashPoint']).value ||
      !!this.form.get(['flashPointRange']).value;
    const hasAnyPercentSolids = (): boolean =>
      !!this.form.get(['percentSolids']).value ||
      !!this.form.get(['percentSolidsLow']).value ||
      !!this.form.get(['percentSolidsHigh']).value;

    this.form = this.formBuilder.group({
      name:
        this.isLegacy === false
          ? [wasteStream.name, [Validators.required]]
          : [wasteStream.name],
      stateApprovalWasteCode: [
        wasteStream.stateApprovalWasteCode,
        [Validators.required],
      ],
      stateApprovalWasteCodeNotApplicable: [
        wasteStream.stateApprovalWasteCodeNotApplicable,
      ],
      wasteType:
        this.isLegacy === false
          ? [wasteStream.wasteType, [Validators.required]]
          : [wasteStream.wasteType],
      frequency:
        this.isLegacy === false
          ? [wasteStream.frequency, [Validators.required]]
          : [wasteStream.frequency],
      physicalState:
        this.isLegacy === false
          ? [wasteStream.physicalState, [Validators.required]]
          : [wasteStream.physicalState],
      estimatedVolume:
        this.isLegacy === false
          ? [
              wasteStream.estimatedVolume,
              [
                Validators.required,
                Validators.max(999999999),
                FormValidators.integer,
              ],
            ]
          : [wasteStream.estimatedVolume],
      containsFreeLiquids:
        this.isLegacy === false
          ? [wasteStream.containsFreeLiquids, [Validators.required]]
          : [wasteStream.containsFreeLiquids],
      unitOfMeasure:
        this.isLegacy === false
          ? [wasteStream.unitOfMeasure, [Validators.required]]
          : [wasteStream.unitOfMeasure],
      methodOfShipment:
        this.isLegacy === false
          ? [wasteStream.methodOfShipment, [Validators.required]]
          : [wasteStream.methodOfShipment],
      otherMethodOfShipment: [wasteStream.otherMethodOfShipment],
      disposalConsideration:
        this.isLegacy === false
          ? [wasteStream.disposalConsideration, [Validators.required]]
          : [wasteStream.disposalConsideration],

      percentSolidsLow:
        this.isLegacy === false
          ? [
              wasteStream.percentSolidsLow,
              [
                Validators.min(0),
                Validators.max(100),
                FormValidators.decimal(),
                FormValidators.lessThan(
                  () => +this.form.get('percentSolidsHigh').value
                ),
                FormValidators.requiredIf(
                  // both high and low must be populated, if provided
                  () => !!this.form.get('percentSolidsHigh').value
                ),
                FormValidators.requiredIf(
                  // if none of the percent solids are populated
                  () => !hasAnyPercentSolids()
                ),
              ],
            ]
          : [wasteStream.percentSolidsLow],
      percentSolidsHigh:
        this.isLegacy === false
          ? [
              wasteStream.percentSolidsHigh,
              [
                Validators.min(0),
                Validators.max(100),
                FormValidators.decimal(),
                FormValidators.greaterThan(
                  () => +this.form.get('percentSolidsLow').value
                ),
                FormValidators.requiredIf(
                  // both high and low must be populated, if provided
                  () => !!this.form.get('percentSolidsLow').value
                ),
                FormValidators.requiredIf(
                  // if none of the percent solids are populated
                  () => !hasAnyPercentSolids()
                ),
              ],
            ]
          : [wasteStream.percentSolidsHigh],
      percentSolids:
        this.isLegacy === false
          ? [
              wasteStream.percentSolids,
              [
                Validators.min(0),
                Validators.max(100),
                FormValidators.decimal(),
                FormValidators.requiredIf(
                  // if none of the percent solids are populated
                  () => !hasAnyPercentSolids()
                ),
              ],
            ]
          : [wasteStream.percentSolids],
      phRange:
        this.isLegacy === false
          ? [wasteStream.phRange, FormValidators.requiredIf(() => !hasAnyPh())]
          : [wasteStream.phRange],
      phExact:
        this.isLegacy === false
          ? [
              wasteStream.phExact,
              [
                Validators.min(0),
                Validators.max(14),
                FormValidators.decimal(),
                FormValidators.requiredIf(() => !hasAnyPh()),
              ],
            ]
          : [wasteStream.phExact],
      phNotApplicable: [wasteStream.phNotApplicable],
      percentSolidNotApplicable: [wasteStream.percentSolidNotApplicable],
      flashPoint:
        this.isLegacy === false
          ? [
              wasteStream.flashPoint,
              [
                Validators.pattern(/^[1-9][0-9]*$/),
                FormValidators.integer,
                FormValidators.requiredIf(() => !hasAnyFlashPoint()),
                // these validations are disabled for now, as we need to allow for 'N/A'
                // Validators.pattern(/^[0-9>< ]*$/)
                // FormValidators.decimal()
                // FormValidators.requiredIf(
                //   // if physical state is liquid
                //   () => this.form.getControl('containsFreeLiquids').value === true
                // )
              ],
            ]
          : [wasteStream.flashPoint],
      flashPointRange:
        this.isLegacy === false
          ? [
              wasteStream.flashPointRange,
              FormValidators.requiredIf(() => !hasAnyFlashPoint()),
            ]
          : [wasteStream.flashPointRange],
      flashPointNotApplicable: [wasteStream.flashPointNotApplicable],
      processGeneratingWaste:
        this.isLegacy === false
          ? [
              wasteStream.processGeneratingWaste,
              [Validators.required, Validators.maxLength(this.maxCharCount)],
            ]
          : [wasteStream.processGeneratingWaste],
    });
    this.charCount = this.form.get('processGeneratingWaste')?.value.length || 0;

    // update conditional validation state for physical state
    // these validations are disabled for now, as we need to allow for 'N/A'
    // (this.form.getControl('containsFreeLiquids') as FormControl).value$
    //   .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
    //   .subscribe(() => {
    //     this.form.getControl('percentSolids').updateValueAndValidity();
    //     this.form.getControl('percentSolidsLow').updateValueAndValidity();
    //     this.form.getControl('percentSolidsHigh').updateValueAndValidity();
    //     this.form.getControl('phExact').updateValueAndValidity();
    //     this.form.getControl('phLow').updateValueAndValidity();
    //     this.form.getControl('phHigh').updateValueAndValidity();
    //     this.form.getControl('flashPoint').updateValueAndValidity();
    //   });

    // update conditional validation state for exact % solids
    this.form
      .get('percentSolids')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('percentSolidsLow').updateValueAndValidity();
        this.form.get('percentSolidsHigh').updateValueAndValidity();
      });

    // update conditional validation state for low % solids
    this.form
      .get('percentSolidsLow')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('percentSolids').updateValueAndValidity();
        this.form.get('percentSolidsHigh').updateValueAndValidity();
      });

    // update conditional validation state for high % solids
    this.form
      .get('percentSolidsHigh')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('percentSolids').updateValueAndValidity();
        this.form.get('percentSolidsLow').updateValueAndValidity();
      });

    // update conditional validation state for exact pH
    this.form
      .get('phExact')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('phRange').updateValueAndValidity();
      });

    // update conditional validation state for low pH
    this.form
      .get('phRange')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('phExact').updateValueAndValidity();
      });

    // update conditional validation state for Range Flash Point
    this.form
      .get('flashPointRange')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('flashPoint').updateValueAndValidity();
      });

    // update conditional validation state for Flash Point
    this.form
      .get('flashPoint')
      .value$.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.form.get('flashPointRange').updateValueAndValidity();
      });
  }

  updateCharCount(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    this.charCount = input.value.length;
  }

  public ngOnInit(): void {
    let wasteStream: WasteStreamFormElements =
      SpecialWasteProfileModel.DEFAULT_CONFIG;

    if (this.value) {
      wasteStream = this.value;
    }
    if (this.form) {
      this.form.patchValue(wasteStream, {
        emitEvent: false,
      });
    } else {
      this.initializeForm(wasteStream);
      this.onStateCodeChange();
      this.onFrequencyChanges();
      this.onPhysicalstateChanges();
      this.onMethodOfShipmentChanges();
      this.onStateChange();
      this.onphValueChange();
      this.onSolidValueChange();
      this.onFlashPointValueChange();
      this.onContainFreeLiquids();
    }
    super.ngOnInit();
  }

  errorHandling = (
    controlName: keyof WasteStreamFormElements,
    error: string
  ) => {
    const control = this.form.get(controlName);
    return control?.touched && control.hasError(error);
  };

  stateApprovalWasteCodeErrorHandling = () => {
    const control = this.form.get('stateApprovalWasteCode');
    const invalid = control.invalid;

    if (invalid) control.markAsTouched();
    return invalid;
  };

  onFrequencyChanges = () => {
    const frequencyControl = this.form.get('frequency');

    frequencyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        if (val === 'ongoing') this.volumeLabel = 'Estimated Annual Volume';
        else this.volumeLabel = 'Estimated Project Volume';
      });
  };

  onContainFreeLiquids = () => {
    const physicalStateControl = this.form.get('physicalState');
    const containsFreeLiquidsControls = this.form.get('containsFreeLiquids');
    const flashPointNotApplicable = this.form.get('flashPointNotApplicable');
    const phNotApplicableControl = this.form.get('phNotApplicable');

    const updateValidationState = (
      val: SpecialWasteProfile['containsFreeLiquids']
    ) => {
      if (val && physicalStateControl.value === 'semi-solid') {
        flashPointNotApplicable.setValue(false);
        flashPointNotApplicable.enable({ emitEvent: true });

        phNotApplicableControl.setValue(false);
        phNotApplicableControl.enable({ emitEvent: true });

        this.flashPointNotApplicableNotVisiable = true;
        this.isPhNotApplicable = true;
      } else if (physicalStateControl.value === 'semi-solid') {
        this.flashPointNotApplicableNotVisiable = false;
        this.isPhNotApplicable = false;

        flashPointNotApplicable.setValue(true);
        flashPointNotApplicable.enable({ emitEvent: true });
      }
      if (val) {
        this.onUpdateFreeLiquidValue(physicalStateControl.value);
      } else {
        this.filteredDisposalConsideration = [
          this.landFillOption,
          this.biomremediationOptions,
        ];
      }
    };

    updateValidationState(containsFreeLiquidsControls.value);
    containsFreeLiquidsControls.value$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onUpdateFreeLiquidValue(value: string) {
    if (value === 'liquids-in-consumer-size-packaging') {
      this.filteredDisposalConsideration = [this.landFillOption];
    } else {
      this.filteredDisposalConsideration = [this.solidificationOptions];
    }
  }
  onPhysicalstateChanges = () => {
    const physicalStateControl = this.form.get('physicalState');
    const methodOfShipmentControl = this.form.get('methodOfShipment');
    const disposalConsiderationControl = this.form.get('disposalConsideration');
    const containsFreeLiquidsControls = this.form.get('containsFreeLiquids');
    const flashPointNotApplicable = this.form.get('flashPointNotApplicable');
    const phNotApplicableControl = this.form.get('phNotApplicable');
    const flashPointControl = this.form.get('flashPoint');
    const updateValidationState = (
      val: SpecialWasteProfile['physicalState']
    ) => {
      if (val && (this.form.touched || this.form.dirty)) {
        methodOfShipmentControl.setValue(null);
        disposalConsiderationControl.setValue(null);
      }
      this.showBagged = true;
      this.isLiquid = false;
      this.isPhNotApplicable = false;
      this.showDeepWell = false;
      this.flashPointNotApplicableNotVisiable = false;
      this.showSolidification = true;
      if (flashPointControl.value === 'N/A') {
        flashPointControl.setValue(null, { emitEvent: false });
      }
      switch (val) {
        case 'solid':
          this.showSolidification = false;
          flashPointNotApplicable.setValue(true);
          flashPointNotApplicable.disable({ emitEvent: false });
          containsFreeLiquidsControls.setValue(false);
          this.disableFreeliquids = true;
          flashPointControl.setValue('N/A', { emitEvent: false });
          break;
        case 'powder':
          flashPointNotApplicable.setValue(true);
          flashPointNotApplicable.disable({ emitEvent: false });
          containsFreeLiquidsControls.setValue(false);
          this.disableFreeliquids = true;
          flashPointControl.setValue('N/A', { emitEvent: false });
          break;
        case 'semi-solid':
          this.disableFreeliquids = false;
          if (containsFreeLiquidsControls.value) {
            flashPointNotApplicable.setValue(false);
            flashPointNotApplicable.enable({ emitEvent: true });
            phNotApplicableControl.setValue(false);
            phNotApplicableControl.enable({ emitEvent: true });
            this.flashPointNotApplicableNotVisiable = true;
            this.isPhNotApplicable = true;
          } else {
            phNotApplicableControl.setValue(false);
            phNotApplicableControl.enable();

            this.flashPointNotApplicableNotVisiable = false;
            this.isPhNotApplicable = false;

            flashPointNotApplicable.setValue(true);
            flashPointNotApplicable.enable({ emitEvent: true });
          }
          break;
        case 'liquid':
          this.showBagged = false;
          this.isLiquid = true;
          this.showDeepWell = true;
          this.isPhNotApplicable = true;
          this.flashPointNotApplicableNotVisiable = true;
          flashPointNotApplicable.setValue(false);
          flashPointNotApplicable.enable({ emitEvent: false });
          phNotApplicableControl.setValue(false);
          containsFreeLiquidsControls.setValue(true);
          this.disableFreeliquids = true;
          break;
        case 'liquids-in-nonleaking-consumer-size-packaging':
          this.showBagged = false;
          this.isLiquid = true;
          this.showDeepWell = true;
          this.isPhNotApplicable = true;
          this.flashPointNotApplicableNotVisiable = true;
          flashPointNotApplicable.setValue(false);
          flashPointNotApplicable.enable({ emitEvent: false });
          phNotApplicableControl.setValue(false);
          containsFreeLiquidsControls.setValue(false);
          this.disableFreeliquids = true;
          this.filteredDisposalConsideration = [this.landFillOption];
          break;
      }

      methodOfShipmentControl.updateValueAndValidity();
    };

    updateValidationState(physicalStateControl.value);
    physicalStateControl.value$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onMethodOfShipmentChanges = () => {
    const methodOfShipmentControl = this.form.get('methodOfShipment');
    const otherMethodOfShipmentControl = this.form.get('otherMethodOfShipment');

    const updateValidationState = (
      val: SpecialWasteProfile['methodOfShipment']
    ) => {
      if (val === 'other') {
        this.showOtherShipment = true;
        otherMethodOfShipmentControl.setValidators([Validators.required]);
      } else {
        this.showOtherShipment = false;
        otherMethodOfShipmentControl.setValue(null);
        otherMethodOfShipmentControl.clearValidators();
      }
      otherMethodOfShipmentControl.updateValueAndValidity();
    };

    updateValidationState(methodOfShipmentControl.value);
    methodOfShipmentControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onStateChange = () => {
    const stateApprovalWasteCodeNotApplicableControl = this.form.get(
      'stateApprovalWasteCodeNotApplicable'
    );
    const stateApprovalWasteCodeControl = this.form.get(
      'stateApprovalWasteCode'
    );

    const updateValidationState = (
      val: SpecialWasteProfile['stateApprovalWasteCodeNotApplicable']
    ) => {
      if (val === true) {
        this.isWasteCodeRequired = false;
        stateApprovalWasteCodeControl.setValue(null);
        stateApprovalWasteCodeControl.clearValidators();
      } else {
        this.isWasteCodeRequired = true;
        stateApprovalWasteCodeControl.setValidators([Validators.required]);
      }

      stateApprovalWasteCodeControl.updateValueAndValidity();
    };

    updateValidationState(stateApprovalWasteCodeNotApplicableControl.value);
    stateApprovalWasteCodeNotApplicableControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onphValueChange = () => {
    const phNotApplicableControl = this.form.get(
      'phNotApplicable'
    ) as FormControl<SpecialWasteProfile['phNotApplicable']>;
    const phExactControl = this.form.get('phExact') as FormControl<
      SpecialWasteProfile['phExact']
    >;
    const phRangeControl = this.form.get('phRange') as FormControl<
      SpecialWasteProfile['phRange']
    >;

    const updateValidationState = (
      val: SpecialWasteProfile['phNotApplicable']
    ) => {
      if (val === true) {
        phExactControl.setValue(null);
        phRangeControl.setValue(null);
        phExactControl.disable({ emitEvent: false });
        phRangeControl.disable({ emitEvent: false });
        this.isphToggleEnabled = true;
        this.isphToggle = false;
      } else {
        phExactControl.enable({ emitEvent: false });
        phRangeControl.enable({ emitEvent: false });
        this.isphToggleEnabled = false;
        if (phExactControl.value !== null) {
          this.isphToggle = false;
        } else if (phRangeControl.value !== null) {
          this.isphToggle = true;
        }
      }
      this.form.updateValueAndValidity();
    };

    updateValidationState(phNotApplicableControl.value);
    phNotApplicableControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onSolidValueChange = () => {
    const percentSolidsHighControl = this.form.get(
      'percentSolidsHigh'
    ) as FormControl<SpecialWasteProfile['percentSolidsHigh']>;
    const percentSolidsLowControl = this.form.get(
      'percentSolidsLow'
    ) as FormControl<SpecialWasteProfile['percentSolidsLow']>;
    const percentSolidsControl = this.form.get('percentSolids') as FormControl<
      SpecialWasteProfile['percentSolids']
    >;

    if (percentSolidsControl.value !== null) {
      this.isSolidToggle = false;
    } else if (
      percentSolidsLowControl.value !== null &&
      percentSolidsHighControl.value !== null
    ) {
      this.isSolidToggle = true;
    }
    this.isSolidToggleEnabled = false;
  };

  onFlashPointValueChange = () => {
    const flashPointNotApplicable = this.form.get('flashPointNotApplicable');
    const flashPointRangeControl = this.form.get('flashPointRange');
    const flashPointControl = this.form.get('flashPoint');
    const physicalStateControl = this.form.get('physicalState');

    const updateValidationState = (
      val: SpecialWasteProfile['flashPointNotApplicable']
    ) => {
      if (val === true) {
        flashPointRangeControl.setValue(null);

        if (physicalStateControl.value === 'powder') {
          flashPointControl.setValue('N/A');
        } else {
          flashPointControl.setValue(null);
        }
        flashPointRangeControl.disable({ emitEvent: false });
        flashPointControl.disable({ emitEvent: false });
        this.isFlashPointToggleEnabled = true;
        this.isFlashPointToggle = false;
      } else {
        flashPointControl.enable({ emitEvent: false });
        flashPointRangeControl.enable({ emitEvent: false });
        if (flashPointControl.value !== null) {
          this.isFlashPointToggle = false;
        } else if (flashPointRangeControl.value !== null) {
          this.isFlashPointToggle = true;
        }
        this.isFlashPointToggleEnabled = false;
      }
      this.form.updateValueAndValidity();
    };

    updateValidationState(flashPointNotApplicable.value);
    flashPointNotApplicable.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  public isphToggleChange($event: MatSlideToggleChange) {
    if ($event.checked === true) {
      this.isphToggle = true;
      this.form.controls['phExact'].setValue(null);
    } else {
      this.isphToggle = false;
      this.form.controls['phRange'].setValue(null);
    }
    this.form.updateValueAndValidity();
  }

  public isSolidToggleChange($event: MatSlideToggleChange) {
    if ($event.checked === true) {
      this.isSolidToggle = true;
      this.form.controls['percentSolids'].setValue(null);
    } else {
      this.isSolidToggle = false;
      this.form.controls['percentSolidsLow'].setValue(null);
      this.form.controls['percentSolidsHigh'].setValue(null);
    }
    this.form.updateValueAndValidity();
  }

  public isFlashPointToggleChange($event: MatSlideToggleChange) {
    if ($event.checked === true) {
      this.isFlashPointToggle = true;
      this.form.controls['flashPoint'].setValue(null);
    } else {
      this.isFlashPointToggle = false;
      this.form.controls['flashPointRange'].setValue(null);
    }
    this.form.updateValueAndValidity();
  }

  public physicalStateChange($event: MatCheckboxChange) {
    const flashPointRangeControl = this.form.get('flashPointRange');
    const percentSolidsControl = this.form.get('percentSolids');
    const flashPointControl = this.form.get('flashPoint');
    const flashPointNotApplicable = this.form.get('flashPointNotApplicable');
    const phNotApplicableControl = this.form.get('phNotApplicable');

    if ($event.source.value === 'solid') {
      flashPointRangeControl.setValue(null);
      flashPointControl.setValue(null);
      percentSolidsControl.setValue(null);
    } else if ($event.source.value === 'semi-solid') {
      flashPointNotApplicable.setValue(false);
      flashPointNotApplicable.enable({ emitEvent: true });
      phNotApplicableControl.setValue(false);
      phNotApplicableControl.enable({ emitEvent: true });
      this.flashPointNotApplicableNotVisiable = true;
      this.isPhNotApplicable = true;
    } else if ($event.source.value === 'powder') {
      percentSolidsControl.setValue('100');
      flashPointRangeControl.setValue('N/A');
      flashPointControl.setValue('N/A');
    } else {
      this.isFlashPointToggle = false;
      percentSolidsControl.setValue(null);
      flashPointControl.setValue(null);
    }
  }

  onStateCodeChange = () => {
    const stateApprovalWasteCodeNotApplicableControl = this.form.get(
      'stateApprovalWasteCodeNotApplicable'
    );
    const stateApprovalWasteCodeControl = this.form.get(
      'stateApprovalWasteCode'
    );

    const updateValidationState = (
      val: SpecialWasteProfile['stateApprovalWasteCode']
    ) => {
      if (val) {
        stateApprovalWasteCodeNotApplicableControl.setValue(false, {
          emitEvent: false,
        });
      }
    };

    updateValidationState(stateApprovalWasteCodeControl.value);
    stateApprovalWasteCodeControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  get physicalState() {
    return this.form.getRawValue().physicalState;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.isLegacy === true ||
      changes.profileStatus?.currentValue === 'Not Submitted'
    ) {
      [
        'name',
        'stateApprovalWasteCode',
        'wasteType',
        'frequency',
        'physicalState',
        'estimatedVolume',
        'containsFreeLiquids',
        'unitOfMeasure',
        'methodOfShipment',
        'disposalConsideration',
        'processGeneratingWaste',
      ].forEach((control) => {
        this.form?.controls[control].removeValidators(Validators.required);
        this.form?.updateValueAndValidity();
      });
    } else if (
      this.isLegacy === false &&
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      [
        'name',
        'stateApprovalWasteCode',
        'wasteType',
        'frequency',
        'physicalState',
        'estimatedVolume',
        'containsFreeLiquids',
        'unitOfMeasure',
        'methodOfShipment',
        'disposalConsideration',
        'processGeneratingWaste',
      ].forEach((control) => {
        this.form.controls[control].addValidators(Validators.required);
        this.onStateChange();
        this.form.updateValueAndValidity();
      });
    }
  }
}
